import {Injectable} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, ValidatorFn, Validators } from "@angular/forms";
import {ValidationErrorDescription} from '../validation-error-description';
import {estBicValide, estIbanValide} from '../../coordonnees-bancaires';
import {estEmailValide, estTelephoneValide} from '../../coordonnees-contact';

const PATTERN_ERROR_MESSAGE = 'Format attendu non respecté : "{0}"';
const REQUIRED_ERROR_MESSAGE = '';
const MINLENGTH_ERROR_MESSAGE = 'Nombre de caractères incorrect (min. {0})';
const MAXLENGTH_ERROR_MESSAGE = 'Nombre de caractères incorrect (max. {0})';
const MINMAX_LENGTH_ERROR_MESSAGE = 'Nombre de caractères incorrect (min. {0}, max. {1})';
const POSITIVE_NUMBER_ERROR_MESSAGE = 'La valeur doit être un nombre positif';
const POSITIVE_DECIMAL_ERROR_MESSAGE = 'La valeur doit être un nombre decimal positif (avec {0} à {1} chiffre(s) après la virgule)';
const MIN_VALUE_ERROR_MESSAGE = 'Format attendu : nbr supérieur à 0 à 0,01 près après la virgule';
const MAX_VALUE_ERROR_MESSAGE = 'La valeur ne doit pas dépasser {0}';
const MONETARY_ERROR_MESSAGE = 'Format monétaire attendu : ####,##';
const DEFAULT_MONETARY_DECIMAL = 2;
const EMAIL_FORMAT_MESSAGE = 'Format de l’adresse e-mail invalide';
const NUMERO_TELEPHONE_FRANCAIS = `^(0|\\+33)[1-9]([-. ]?[0-9]{2}){4}$`;
const PHONE_FORMAT_MESSAGE = 'Le format du numéro est incorrect. Format attendu : {0}';
const IBAN_FORMAT_MESSAGE = "L'IBAN est invalide.";
const BIC_FORMAT_MESSAGE = 'Le BIC est invalide.';
const TELEPHONE_INTERNATIONAL_FORMAT_MESSAGE = 'Le numéro de téléphone est invalide.';


@Injectable({
  providedIn: 'root',
})
export class ValidatorsBuilderService {
  constructor() {}

  private strReplace(s: string, ...args: any) {
    return s.replace(/{(\d+)}/g, function(match, number) {
      return typeof args[number] != 'undefined' ? args[number] : match;
    });
  }

  required(message: string = REQUIRED_ERROR_MESSAGE): ValidatorFn {
    return formControl => {
      return Validators.required(formControl)
        ? {required: new ValidationErrorDescription(this.strReplace(message, formControl.value))}
        : null;
    };
  }

  phoneNumber(message: string = PHONE_FORMAT_MESSAGE): ValidatorFn {
    return this.format(NUMERO_TELEPHONE_FRANCAIS, 'téléphonique', message);
  }

  email(message: string = EMAIL_FORMAT_MESSAGE): ValidatorFn {
    return formControl => {
      return Validators.email(formControl) ? {emailFormat: new ValidationErrorDescription(message)} : null;
    };
  }

  /**
   * Email avec extension de domaine obligatoire (`contact@axess` est refusé, contrairement à `email()`),
   * comme le contrôle du back ; un champ vide est accepté.
   */
  emailAvecDomaine(message: string = EMAIL_FORMAT_MESSAGE): ValidatorFn {
    return formControl => {
      return formControl.value && !estEmailValide(formControl.value)
        ? {emailFormat: new ValidationErrorDescription(message)}
        : null;
    };
  }

  /** Téléphone national ou international (+33, +32...), de 6 à 15 chiffres ; un champ vide est accepté. */
  telephoneInternational(message: string = TELEPHONE_INTERNATIONAL_FORMAT_MESSAGE): ValidatorFn {
    return formControl => {
      return formControl.value && !estTelephoneValide(formControl.value)
        ? {telephoneFormat: new ValidationErrorDescription(message)}
        : null;
    };
  }

  /** IBAN (espaces tolérés) ; un champ vide est accepté. */
  iban(message: string = IBAN_FORMAT_MESSAGE): ValidatorFn {
    return formControl => {
      return formControl.value && !estIbanValide(formControl.value)
        ? {ibanFormat: new ValidationErrorDescription(message)}
        : null;
    };
  }

  /** BIC de 8 ou 11 caractères ; un champ vide est accepté. */
  bic(message: string = BIC_FORMAT_MESSAGE): ValidatorFn {
    return formControl => {
      return formControl.value && !estBicValide(formControl.value)
        ? {bicFormat: new ValidationErrorDescription(message)}
        : null;
    };
  }

  format(pattern: string | RegExp, formatAttendu: string, message: string = PATTERN_ERROR_MESSAGE): ValidatorFn {
    return formControl => {
      return Validators.pattern(pattern)(formControl)
        ? {pattern: new ValidationErrorDescription(this.strReplace(message, formatAttendu))}
        : null;
    };
  }

  minLength(limit: number, message: string = MINLENGTH_ERROR_MESSAGE): ValidatorFn {
    return formControl => {
      return Validators.minLength(limit)(formControl)
        ? {pattern: new ValidationErrorDescription(this.strReplace(message, limit))}
        : null;
    };
  }

  maxLength(limit: number, message: string = MAXLENGTH_ERROR_MESSAGE): ValidatorFn {
    return formControl => {
      return Validators.maxLength(limit)(formControl)
        ? {pattern: new ValidationErrorDescription(this.strReplace(message, limit))}
        : null;
    };
  }

  minMaxLength(limitMin: number, limitMax, message: string = MINMAX_LENGTH_ERROR_MESSAGE): ValidatorFn {
    return formControl => {
      return Validators.minLength(limitMin)(formControl)
        ? {pattern: new ValidationErrorDescription(this.strReplace(message, limitMin, limitMax))}
        : Validators.maxLength(limitMax)(formControl)
          ? {pattern: new ValidationErrorDescription(this.strReplace(message, limitMin, limitMax))}
          : null;
    };
  }

  monetary(decimal: number = DEFAULT_MONETARY_DECIMAL, message: string = MONETARY_ERROR_MESSAGE): ValidatorFn {
    return formControl => {
      return Validators.pattern('^\\d+([.,]\\d{1,2})?$')(formControl)
        ? {pattern: new ValidationErrorDescription(message)}
        : null;
    };
  }

  collectErrors(group: UntypedFormGroup): object {
    const concatenation = {}
    Object.keys(group.controls).forEach(key => concatenation[key] = group.get(key).errors)
    return concatenation
  }

  positiveInteger(message: string = POSITIVE_NUMBER_ERROR_MESSAGE): ValidatorFn {
    return this.format('\\d+', message);
  }

  positiveDecimal(maxDecimal: number = 2, minDecimal: number = 1, message: string = ''): ValidatorFn {

    if (maxDecimal < 1 || minDecimal < 1 || maxDecimal < minDecimal) {
      throw (new Error("Au moins un des paramètres en entrée possède une valeur incohérente"));
    }

    if (message === '') {
      message = POSITIVE_DECIMAL_ERROR_MESSAGE.replace('{0}', minDecimal.toString()).replace('{1}', maxDecimal.toString());
    }

    return this.format('\\d+((\\.|\\,)\\d{' + minDecimal + ',' + maxDecimal + '})?', message);
  }

  minValue(minLimit, message: string = MIN_VALUE_ERROR_MESSAGE): ValidatorFn {
    return formControl => {
      return Validators.min(minLimit)(formControl) ? {pattern: new ValidationErrorDescription(message)} : null;
    };
  }

  maxValue(maxLimit, message: string = MAX_VALUE_ERROR_MESSAGE): ValidatorFn {
    return formControl => {
      return Validators.max(maxLimit)(formControl) ? {pattern: new ValidationErrorDescription(this.strReplace(message, maxLimit))} : null;
    };
  }

  custom(validation: (control: UntypedFormControl) => boolean, code: string, message: string): ValidatorFn {
    return (ctrl: UntypedFormControl) => {
      if (!validation(ctrl)) {
        const errObj = {};
        errObj[code] = new ValidationErrorDescription(this.strReplace(message, ctrl.value));
        return errObj;
      } else {
        return null;
      }
    };
  }
}
